import { useState } from "react";

const projectTypes = ["Web Application", "Mobile App", "AI/ML Solution", "Digital Marketing Campaign", "Cloud Infrastructure", "Cybersecurity Audit", "Data Analytics Platform", "Other"];
const budgetOptions = ["Under $5,000", "$5,000 – $20,000", "$20,000 – $50,000", "$50,000+", "Not sure yet"];

type FormData = { name: string; email: string; company: string; projectType: string; budget: string; description: string; inspiration: string; };
const initial: FormData = { name: "", email: "", company: "", projectType: "", budget: "", description: "", inspiration: "" };

export default function ProjectRequestForm() {
  const [form, setForm] = useState<FormData>(initial);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setForm((p) => ({ ...p, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (form.description.length > 500) { setError("Description must be 500 characters or less."); return; }
    setError(""); setSubmitting(true);
    try {
      await fetch("https://readdy.ai/api/form/d79ubtr2hsar0k5m0tq0", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams({ ...form }).toString(),
      });
      setSubmitted(true);
    } catch (_e) { setError("Something went wrong. Please try again."); }
    finally { setSubmitting(false); }
  };

  return (
    <section className="py-20 bg-white" id="project-request">
      <div className="max-w-3xl mx-auto px-4 md:px-8">
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 border border-[#C8F135]/40 rounded-full px-4 py-1.5 mb-4">
            <span className="text-xs font-semibold uppercase tracking-wider" style={{color: "#9bc400"}}>Start a Project</span>
          </div>
          <h2 className="text-3xl md:text-4xl font-black text-gray-900 mb-3">
            Let&apos;s Build Your <span style={{color: "#9bc400"}}>Next Project</span>
          </h2>
          <p className="text-gray-500 text-sm">Tell us what you&apos;re envisioning and we&apos;ll make it a reality.</p>
        </div>

        <div className="bg-black rounded-2xl p-8">
          {submitted ? (
            <div className="flex flex-col items-center text-center py-10">
              <div className="w-14 h-14 flex items-center justify-center rounded-full bg-[#C8F135]/20 mb-4">
                <i className="ri-check-line text-[#C8F135] text-2xl" />
              </div>
              <h3 className="text-xl font-black text-white mb-2">Project Request Received!</h3>
              <p className="text-gray-400 text-sm">We&apos;ll review your request and reach out to <strong className="text-white">{form.email}</strong> within 24 hours.</p>
            </div>
          ) : (
            <form data-readdy-form id="portfolio-project-request-form" onSubmit={handleSubmit} className="flex flex-col gap-5">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <label className="text-xs text-gray-400 font-medium mb-1.5 block">Full Name *</label>
                  <input type="text" name="name" value={form.name} onChange={handleChange} required placeholder="John Smith"
                    className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-[#C8F135] transition-colors" />
                </div>
                <div>
                  <label className="text-xs text-gray-400 font-medium mb-1.5 block">Email *</label>
                  <input type="email" name="email" value={form.email} onChange={handleChange} required placeholder="john@company.com"
                    className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-[#C8F135] transition-colors" />
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <label className="text-xs text-gray-400 font-medium mb-1.5 block">Company</label>
                  <input type="text" name="company" value={form.company} onChange={handleChange} placeholder="Your company"
                    className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-[#C8F135] transition-colors" />
                </div>
                <div>
                  <label className="text-xs text-gray-400 font-medium mb-1.5 block">Project Type *</label>
                  <select name="projectType" value={form.projectType} onChange={handleChange} required
                    className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-sm text-white focus:outline-none focus:border-[#C8F135] transition-colors appearance-none cursor-pointer">
                    <option value="" className="bg-gray-900">Select type</option>
                    {projectTypes.map((o) => <option key={o} value={o} className="bg-gray-900">{o}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label className="text-xs text-gray-400 font-medium mb-1.5 block">Budget Range</label>
                <select name="budget" value={form.budget} onChange={handleChange}
                  className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-sm text-white focus:outline-none focus:border-[#C8F135] transition-colors appearance-none cursor-pointer">
                  <option value="" className="bg-gray-900">Select budget</option>
                  {budgetOptions.map((o) => <option key={o} value={o} className="bg-gray-900">{o}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs text-gray-400 font-medium mb-1.5 block">
                  Project Description * <span className="text-gray-600">({form.description.length}/500)</span>
                </label>
                <textarea name="description" value={form.description} onChange={handleChange} required maxLength={500} rows={4}
                  placeholder="Describe your project vision, goals, and key features you need..."
                  className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-[#C8F135] transition-colors resize-none" />
              </div>
              <div>
                <label className="text-xs text-gray-400 font-medium mb-1.5 block">Inspiration / References <span className="text-gray-600">(optional)</span></label>
                <input type="text" name="inspiration" value={form.inspiration} onChange={handleChange}
                  placeholder="Links to websites or apps you like..."
                  className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-[#C8F135] transition-colors" />
              </div>
              {error && <p className="text-red-400 text-xs">{error}</p>}
              <button type="submit" disabled={submitting}
                className="w-full py-4 rounded-xl bg-[#C8F135] text-black font-bold text-sm cursor-pointer hover:bg-[#b5e000] transition-colors disabled:opacity-60 flex items-center justify-center gap-2">
                {submitting ? <><i className="ri-loader-4-line animate-spin" /> Sending...</> : <><i className="ri-rocket-line" /> Submit Project Request</>}
              </button>
            </form>
          )}
        </div>
      </div>
    </section>
  );
}
